import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProvider } from 'assets/providers/data/DataProvider';
import { convertValueToArray } from 'assets/utils/data/Data';
import { combineStrings } from 'assets/utils/data/String';
import { defaultLimit, defaultOffset } from 'config/Api.Config';
import { claimsPermissions } from 'config/Auth.Config';
import { compact, keys, isEmpty } from 'lodash';
import Claim from 'models/claims/claim/Model.Claim';
import claimDeleteApi from 'models/claims/claim/delete/Api.Claim.Delete';
import ClaimJob from 'models/claims/claimJob/Model.ClaimJob';
import EmailTemplate from 'models/core/claimEmailTemplate/Model.ClaimEmailTemplate';
import User from 'models/core/user/Model.User';
import Dealer from 'models/dealerManagement/dealer/Model.Dealer';
import Product from 'models/productManagement/product/Model.Product';
import VehicleMake from 'models/productManagement/vehicleMake/Model.VehicleMake';
import { useMemo, useState, useEffect } from 'react';
import { claimIndexRoute } from './Claim.Index';
import { AuthPermission } from 'config/Auth.Config';
import { ApiHeaderApplication, HttpStatus } from 'config/Api.Config';
import Permission from 'models/core/permission/Model.Permission';
import { getClaimOwnershipRules } from '../../helper/Claim.OwnershipRules';
import claimBulkOwnershipUpdateApi from 'models/claims/claim/bulkOwnershipUpdate/Api.Claim.BulkOwnershipUpdate';
import { addLog } from 'assets/components/feedback/Feedback';

export enum ClaimAction {
  ADD = 'add',
  EDIT_SIMPLE = 'editSimple',
  DELETE = 'delete',
}
export type VerifyClaimAction = Utils.VerifyExtends<Module.Claims.Claim.Actions, Utils.ValueOf<typeof ClaimAction>>;
export type ClaimListType = 'claims' | 'claimJobs';
export function useClaimRouter() {
  return usePageRouter<Module.Claims.Claim.Params, Module.Claims.Claim.Query>({ route: claimIndexRoute });
}
export default function useClaim() {
  const { lang } = getLocales();
  const pageRouter = useClaimRouter();

  const [selectedClaims, setSelectedClaims] = useState<Model.IClaim[]>([]);

  const emailTemplatesApi = useApi({ action: EmailTemplate.list });
  const emailTemplates = emailTemplatesApi.payload?.data;

  const statusInfoApi = useApi({ action: Claim.getStatusFlowAndRequirements });
  const statusInfo = statusInfoApi.payload;

  const { user, permissions, currentPermissions, userId } = useAuthStore();

  const claimId = pageRouter?.params?.id ? parseInt(pageRouter.params.id) : undefined;

  const isClaimAdmin = permissions?.CLAIM_ADMIN;
  const canView = permissions?.CLAIM_ADMIN || permissions?.VIEW_CLAIM;
  const isEditable = isClaimAdmin || permissions?.EDIT_CLAIM;
  const canAddClaim = isClaimAdmin || permissions?.CREATE_NEW_CLAIM;
  const hasAnyPermission = claimsPermissions.some((it) => currentPermissions.includes(it));

  //#region Api calls
  const listApi = useApi(
    {
      action: Claim.list,
      default: {
        limit: defaultLimit,
        offset: defaultOffset,
        reporterId: permissions?.VIEW_ONLY_OWNED_CLAIMS ? user?.id : undefined,
        dealerId: convertValueToArray(user.dealers).map((it) => it.id),
        distributorId: convertValueToArray(user.distributorId),
      },
      body: {
        ...pageRouter.query.list,
      },
      callback: (req) => {
        pageRouter.updateQuery({ ...pageRouter.query, list: req });
      },
      wait: !hasAnyPermission,
    },
    [JSON.stringify(pageRouter.query.list)]
  );

  const deleteApi = useApi({
    action: claimDeleteApi,
    callback: () => {
      pageRouter.clear();
      listApi.execute((body) => body);
    },
    wait: true,
  });

  //#endregion

  //#region Variables
  const claim = listApi.payload?.data?.find((it) => it.id === claimId);
  const isDeletable =
    !!(isEditable && claim && !claim.claimJobCount && !claim.claimDocuments?.length) &&
    (isClaimAdmin || permissions?.DELETE_CLAIM);
  const isClaimOwner = claim?.ownerId === userId;
  //#endregion

  const productsApi = useApi({
    action: Product.list,
    wait: true,
  });
  const vehicleMakeApi = useApi({ action: VehicleMake.list, wait: true });
  const userApi = useApi({ action: User.list, default: { isDisabled: false }, wait: true });
  const dealersApi = useApi({
    action: Dealer.list,
    default: {
      id: compact(user?.dealers?.map((it) => it.id)),
      distributorId: user?.distributorId ? [user?.distributorId] : undefined,
      orderBy: 'name',
      orderType: 'asc',
    },
    wait: true,
  });

  const productApi = useApi({
    action: Product.find,
    wait: !claim?.contract?.productId,
    body: { id: claim?.contract?.productId },
  });
  const product = productApi.payload;

  const statuses = useMemo(() => {
    return keys(statusInfo).map((status) => {
      const [primary, secondary, reason] = (status ? lang[status] : status).split('_');
      const mainStatus = combineStrings(' - ', primary, secondary);
      return {
        id: status,
        title: reason ?? mainStatus,
        subtitle: mainStatus,
      };
    });
  }, [statusInfo]);

  const jobListApi = useApi(
    {
      action: ClaimJob.list,
      default: {
        claimId: claimId,
      },
    },
    [JSON.stringify(pageRouter.query.list)]
  );

  const bulkOwnerUserListApi = useApi({
    action: User.list,
    body: {
      isDisabled: false,
      distributorId: user?.distributorId ? [user?.distributorId] : undefined,
      dealerId: compact(user?.dealers?.map((it) => it.id)),
    },
    wait: true,
  });

  const bulkOwnerPermissionListApi = useApi({
    action: Permission.list,
    body: {
      applicationId: ApiHeaderApplication.claims,
    },
    callback: (req, res) => {
      const applicationId = [req.applicationId];

      if (res?.status === HttpStatus.ok) {
        const permissionId = res?.payload?.data
          ?.filter((it) => it.key === AuthPermission.claimAdmin || it.key === AuthPermission.updateClaimOwner)
          ?.map((it) => it.id);

        if (!isEmpty(permissionId)) {
          bulkOwnerUserListApi.execute((b) => ({ ...b, permissionId, applicationId }));
        } else {
          bulkOwnerUserListApi.execute((b) => ({ ...b, applicationId }));
        }
      } else {
        bulkOwnerUserListApi.execute((b) => ({ ...b, applicationId }));
      }
    },
  });

  useEffect(() => {
    if (hasAnyPermission) {
      bulkOwnerPermissionListApi.execute((b) => b);
    }
  }, [hasAnyPermission]);

  function canChangeClaimOwnership(claim: Model.IClaim) {
    const rules = getClaimOwnershipRules({
      claim,
      emailTemplates,
      statusInfo,
      currentPermissions,
      isClaimAdmin,
      hasPermission: true, // ili tvoja stvarna permission provjera za ownership
    });

    return rules.canChangeOwnership;
  }

  async function getAllSelectedClaims() {
    const newQuery = { ...pageRouter?.query?.list };
    delete newQuery.limit;
    delete newQuery.offset;

    const res = await Claim.list(newQuery);

    const filteredClaims = res?.payload?.data ?? [];
    const selectableClaims = filteredClaims.filter((it) => canChangeClaimOwnership(it));

    return selectedClaims?.length === selectableClaims?.length ? [] : selectableClaims;
  }

  async function bulkChangeClaimOwner(ownerId: number) {
    if (!selectedClaims?.length) {
      return addLog({ warning: 'Select at least one claim.' });
    }

    const allowedClaims = selectedClaims.filter((it) => canChangeClaimOwnership(it));

    if (!allowedClaims.length) {
      return addLog({
        error: 'No selected claims allow ownership change.',
      });
    }

    const res = await claimBulkOwnershipUpdateApi({
      ownerId,
      claimIds: allowedClaims.map((it) => it.id),
    });

    if (res?.status === HttpStatus.ok) {
      addLog({
        success:
          allowedClaims.length === selectedClaims.length
            ? 'Claim owner successfully changed.'
            : 'Owner changed for allowed claims only.',
      });

      setSelectedClaims([]);
      listApi.execute((b) => b);
    } else {
      addLog({ error: res?.message ?? lang.saveError });
    }
  }

  const hasSelectedClaims = !!selectedClaims?.length;

  const hasUnchangeableSelectedClaims = useMemo(() => {
    return selectedClaims?.some((it) => !canChangeClaimOwnership(it));
  }, [selectedClaims, emailTemplates, statusInfo, currentPermissions, isClaimAdmin]);

  const areAllVisibleClaimsSelected = useMemo(() => {
    const visibleSelectableClaims = listApi?.payload?.data?.filter((it) => canChangeClaimOwnership(it)) ?? [];

    if (!visibleSelectableClaims.length) return false;

    return visibleSelectableClaims.every((claim) => selectedClaims?.some((it) => it.id === claim.id));
  }, [listApi?.payload?.data, selectedClaims, emailTemplates, statusInfo, currentPermissions, isClaimAdmin]);

  return {
    selectedClaims,
    setSelectedClaims,
    getAllSelectedClaims,
    bulkChangeClaimOwner,
    bulkOwnerUserListApi,
    canChangeClaimOwnership,
    hasSelectedClaims,
    hasUnchangeableSelectedClaims,
    areAllVisibleClaimsSelected,
    hasAnyPermission,
    canView,
    listApi,
    jobListApi,
    pageRouter,
    isEditable,
    canAddClaim,
    isClaimAdmin,
    isClaimOwner,
    statusInfo,
    emailTemplates,
    productsApi,
    userApi,
    vehicleMakeApi,
    dealersApi,
    statuses,
    deleteApi,
    claim,
    product,
    isDeletable,
    views: {
      [ClaimAction.DELETE]: pageRouter.params.action === ClaimAction.DELETE && isDeletable,
      [ClaimAction.EDIT_SIMPLE]: pageRouter.params.action === ClaimAction.EDIT_SIMPLE && isEditable,
    },
  };
}

export function useClaimProvider() {
  return useDataProvider<ReturnType<typeof useClaim>>();
}
